import { Component, ComponentRef, OnInit } from '@angular/core';
import {
  BulkActionAvailabilityCheckResult,
  BulkActionHandler,
  bulkActionHandler,
  BulkActionHandlerFactory,
} from '@app/shared/components/bulk-actions';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { FormMapping } from '@app/utils/form-mapping/decorators/form-mapping';
import { BooleanInput, Required } from '@app/utils/form-mapping/helpers/default-attributes';
import { FormMappingModel } from '@app/utils/form-mapping/models/form-mapping-model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { FormGroupTyped } from '../../../../../types/typed-forms';

@FormMapping()
class SingleChoiceBulkActionHandlerForm extends FormMappingModel {
  @BooleanInput({
    initialValue: true,
  })
  @Required
  value: any;
}

interface SingleChoiceBulkActionHandlerOptions<T> {
  checkAvailability?: (entry: T) => BulkActionAvailabilityCheckResult;
  choices: TypeaheadEntry[];
  label: string;
  process: (entry: T, chosenValue: any) => Observable<any>;
}

@UntilDestroy()
@Component({
  selector: 'portal-single-choice-bulk-action-handler',
  templateUrl: './single-choice-bulk-action-handler.component.html',
  standalone: false,
})
export class SingleChoiceBulkActionHandlerComponent<T> implements BulkActionHandler<T>, OnInit {
  options: SingleChoiceBulkActionHandlerOptions<T>;

  checkAvailability = (entry) => {
    if (this.options.checkAvailability != null) {
      return this.options.checkAvailability(entry);
    } else {
      return BulkActionAvailabilityCheckResult.Available;
    }
  };

  disable = () => {
    this.form.disable();
  };

  process: (entry: T) => Observable<void> = (entry) => {
    return this.options.process(entry, this.form.value.value);
  };

  form: FormGroupTyped<SingleChoiceBulkActionHandlerForm>;
  title!: string;

  ngOnInit(): void {
    this.form = SingleChoiceBulkActionHandlerForm.formGroup();
  }
}

/**
 * Helper to create a simple bulk action handler that calls the given processing function.
 * Use this if you do not need any additional configuration (e.g. when activating the ip filter for
 * a user).
 * @param options Options to configure the bulk action handler
 */
export function singleChoiceBulkActionHandler<T>(
  options: SingleChoiceBulkActionHandlerOptions<T>
): BulkActionHandlerFactory<T> {
  return (node: Node) => {
    const handlerFactory = bulkActionHandler(SingleChoiceBulkActionHandlerComponent);
    const componentRef = handlerFactory(node) as ComponentRef<SingleChoiceBulkActionHandlerComponent<T>>;

    componentRef.instance.options = options;

    return componentRef;
  };
}
